import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Briefcase, FileText, UserCheck, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../../config/api';

const StatCard = ({ title, value, icon: Icon, color, trend }) => {
    const colorClasses = {
        'brand-teal': 'bg-brand-teal/10 text-brand-teal',
        'blue-500': 'bg-blue-500/10 text-blue-500',
        'purple-500': 'bg-purple-500/10 text-purple-500',
        'emerald-500': 'bg-emerald-500/10 text-emerald-500',
    };

    return (
        <motion.div
            whileHover={{ y: -5 }}
            className="bg-white border border-slate-100 p-6 rounded-2xl flex items-center space-x-4 shadow-sm"
        >
            <div className={`p-3 rounded-xl ${colorClasses[color] || 'bg-gray-500/10 text-gray-500'}`}>
                <Icon className="w-6 h-6" />
            </div>
            <div className="flex-1">
                <p className="text-slate-500 text-sm">{title}</p>
                <div className="flex items-center gap-2">
                    <h3 className="text-2xl font-bold text-dark-text mt-1">{value}</h3>
                    {trend && (
                        <span className={`text-xs font-medium ${trend > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {trend > 0 ? '+' : ''}{trend}%
                        </span>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

const DashboardOverview = () => {
    const [inquiries, setInquiries] = useState([]);
    const [projects, setProjects] = useState([]);
    const [applications, setApplications] = useState([]);
    const [careerJobs, setCareerJobs] = useState([]);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        const token = localStorage.getItem('admin_token');
        const headers = { 
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
        };

        try {
            // Fetch Inquiries
            const inquiriesRes = await axios.get(`${API_URL}/contact-inquiries`, { headers });
            const rawInquiries = Array.isArray(inquiriesRes.data.data) ? inquiriesRes.data.data : [];
            // Normalize inquiries to handle potential snake_case from backend
            const normalizedInquiries = rawInquiries.map(iq => ({
                id: iq.id,
                firstName: iq.firstName || iq.first_name || '',
                lastName: iq.lastName || iq.last_name || '',
                date: iq.createdAt || iq.created_at || iq.date || '',
                status: iq.status || 'New'
            }));
            setInquiries(normalizedInquiries);

            // Fetch Projects
            const projectsRes = await axios.get(`${API_URL}/projects`, { headers });
            let projectsData = [];
            if (Array.isArray(projectsRes.data)) projectsData = projectsRes.data;
            else if (Array.isArray(projectsRes.data.projects)) projectsData = projectsRes.data.projects;
            else if (Array.isArray(projectsRes.data.data)) projectsData = projectsRes.data.data;
            
            setProjects(projectsData.filter(p => p && typeof p === 'object'));

            // Fetch Applications
            const applicationsRes = await axios.get(`${API_URL}/career-applications`, { headers });
            setApplications(Array.isArray(applicationsRes.data) ? applicationsRes.data : []);

        } catch (error) {
            console.error("Dashboard Fetch Error:", error);
            if (error.response?.status === 401) {
                localStorage.removeItem('admin_token');
                localStorage.removeItem('isAdminAuthenticated');
                window.location.href = '/admin/login';
            }
        }

        // Still local for now
        setCareerJobs(JSON.parse(localStorage.getItem('career_jobs') || '[]'));
    };

    return (
        <div className="space-y-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard title="Total Inquiries" value={inquiries.length} icon={MessageSquare} color="brand-teal" />
                <StatCard title="Projects" value={projects.length} icon={Briefcase} color="blue-500" />
                <StatCard title="Applications" value={applications.length} icon={FileText} color="purple-500" />
                <StatCard title="Active Roles" value={careerJobs.length} icon={UserCheck} color="emerald-500" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-bold text-dark-text">Recent Inquiries</h2>
                        <Link to="/admin/inquiries" className="text-sm text-brand-teal hover:underline">View All</Link>
                    </div>
                    <div className="space-y-4">
                        {inquiries.slice(0, 4).map((iq) => (
                            <div key={iq.id} className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-100">
                                <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 rounded-full bg-brand-teal/20 flex items-center justify-center text-brand-teal">
                                        <Users className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-dark-text">{iq.firstName} {iq.lastName}</p>
                                        <p className="text-xs text-slate-500">
                                            {iq.date ? new Date(iq.date).toLocaleDateString() : 'No date'}
                                        </p>
                                    </div>
                                </div>
                                <span className={`px-3 py-1 text-xs rounded-full ${iq.status === 'New' ? 'bg-brand-teal/10 text-brand-teal' : 'bg-gray-500/10 text-gray-500'}`}>{iq.status}</span>
                            </div>
                        ))}
                        {inquiries.length === 0 && <p className="text-center py-4 text-gray-500">No inquiries yet.</p>}
                    </div>
                </div>

                <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
                    <h2 className="text-lg font-bold text-dark-text mb-6">Recent Applications</h2>
                    <div className="space-y-4">
                        {applications.slice(0, 4).map((app) => (
                            <div key={app.id} className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-100 active:scale-95 transition-all">
                                <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-500">
                                        <FileText className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-dark-text">{app.first_name} {app.last_name}</p>
                                        <p className="text-[10px] text-slate-500 uppercase tracking-widest">{app.job_role}</p>
                                    </div>
                                </div>
                                <span className="text-[10px] text-gray-500">
                                    {app.created_at ? new Date(app.created_at).toLocaleDateString() : 'No date'}
                                </span>
                            </div>
                        ))}
                        {applications.length === 0 && <p className="text-center py-4 text-gray-500">No applications yet.</p>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardOverview;
